/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { GitMerge, Check, X, RefreshCw } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { useToast } from '../../hooks/use-toast';
import { GitLabConfig } from '../../types';

interface ConnectionStatus {
  tested: boolean;
  success: boolean;
  error?: string;
  user?: string;
}

export function GitLabSettings() {
  const { 
    gitlabConfig, 
    updateGitLabConfig, 
    clearGitLabConfig,
    isGitLabConfigured 
  } = useApp();
  const { toast } = useToast();

  const [formData, setFormData] = useState<GitLabConfig>({
    accessToken: '',
    instanceUrl: 'https://gitlab.com',
    useSSH: false
  });

  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({ 
    tested: false, 
    success: false 
  });

  // Load existing config
  useEffect(() => {
    if (gitlabConfig) {
      setFormData(gitlabConfig);
    }
  }, [gitlabConfig]);

  const handleInputChange = (field: keyof GitLabConfig, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setConnectionStatus({ tested: false, success: false });
  };

  const testConnection = async () => {
    if (!formData.accessToken) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide an access token'
      });
      return;
    }

    setIsTestingConnection(true);
    
    try {
      const tempService = new (await import('../../services/gitlabService')).GitLabService(formData);
      const result = await tempService.testConnection();
      
      if (result.success) {
        setConnectionStatus({ 
          tested: true, 
          success: true,
          user: result.user?.username
        });
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          error: result.error
        });
      }
    } catch (error: any) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const saveConfiguration = () => {
    if (connectionStatus.tested && !connectionStatus.success) {
      const confirmed = confirm(
        'The connection test failed, but you can still save this configuration. ' +
        'You may need to verify the settings later. Do you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    updateGitLabConfig(formData);
    toast({
      title: 'Configuration saved',
      description: 'Your GitLab settings have been updated.',
    });
  };

  const clearConfiguration = () => {
    if (confirm('Are you sure you want to clear the GitLab configuration?')) {
      clearGitLabConfig();
      setFormData({
        accessToken: '',
        instanceUrl: 'https://gitlab.com',
        useSSH: false
      });
      setConnectionStatus({ tested: false, success: false });
    }
  };

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <GitMerge size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>GitLab Integration</h3>
      </div>

      <div className="form-group">
        <label htmlFor="gitlab-instance-url">GitLab Instance URL</label>
        <input
          id="gitlab-instance-url"
          type="url"
          placeholder="https://gitlab.com"
          value={formData.instanceUrl || 'https://gitlab.com'}
          onChange={(e) => handleInputChange('instanceUrl', e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
          Your GitLab instance URL (e.g., https://gitlab.com for GitLab.com or https://gitlab.yourcompany.com for self-hosted)
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="gitlab-token">Personal Access Token</label>
        <input
          id="gitlab-token"
          type="password"
          placeholder="glpat-your_access_token_here"
          value={formData.accessToken}
          onChange={(e) => handleInputChange('accessToken', e.target.value)}
          disabled={formData.useSSH}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            opacity: formData.useSSH ? 0.6 : 1
          }}
        />
        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
          Create a Personal Access Token at{' '}
          <a href={`${formData.instanceUrl || 'https://gitlab.com'}/-/user_settings/personal_access_tokens`} target="_blank" rel="noopener noreferrer">
            your GitLab settings
          </a>
          {' '}with <code>api</code> and <code>read_repository</code> scopes
        </small>
      </div>

      {/* SSH Authentication Option */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: '12px', 
        marginBottom: '16px',
        padding: '12px',
        background: '#fff',
        borderRadius: '6px',
        border: '1px solid #e5e7eb'
      }}>
        <input
          id="gitlab-use-ssh"
          type="checkbox"
          checked={formData.useSSH || false}
          onChange={(e) => handleInputChange('useSSH', e.target.checked)}
          style={{ marginTop: '4px' }}
        />
        <div>
          <label htmlFor="gitlab-use-ssh" style={{ fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>
            Use SSH for Git operations
          </label>
          <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
            Enable this if you have an SSH key configured with GitLab. When enabled, Git operations 
            will use SSH URLs (git@host:...) instead of HTTPS, and you won't need a Personal Access Token 
            for cloning and pushing. Note: You still need a token to browse repositories in the GitLab API.
          </small>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <button
          className="btn"
          onClick={testConnection}
          disabled={isTestingConnection || !formData.accessToken}
          style={{
            width: 'auto',
            padding: '8px 16px',
            fontSize: '14px'
          }}
        >
          {isTestingConnection ? (
            <>
              <RefreshCw size={16} style={{ marginRight: '8px' }} className="spin" />
              Testing Connection...
            </>
          ) : (
            'Test Connection'
          )}
        </button>

        {connectionStatus.tested && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center' }}>
            {connectionStatus.success ? (
              <>
                <Check size={16} style={{ color: '#28a745', marginRight: '8px' }} />
                <span className="status-badge success">
                  Connected as {connectionStatus.user}
                </span>
              </>
            ) : (
              <>
                <X size={16} style={{ color: '#dc3545', marginRight: '8px' }} />
                <span className="status-badge error">
                  {connectionStatus.error || 'Connection failed'}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '16px',
        padding: '12px',
        background: '#e9ecef',
        borderRadius: '4px',
        fontSize: '13px',
        color: '#495057'
      }}>
        <strong>Note:</strong> Once configured, you can connect repositories to individual products in the Products page.
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button
          className="btn"
          onClick={saveConfiguration}
          disabled={!formData.accessToken}
          style={{
            padding: '8px 16px',
            fontSize: '14px'
          }}
        >
          Save GitLab Configuration
        </button>
        
        {isGitLabConfigured && (
          <button
            className="btn btn-secondary"
            onClick={clearConfiguration}
            style={{
              padding: '8px 16px',
              fontSize: '14px'
            }}
          >
            Clear GitLab Configuration
          </button>
        )}
      </div>
    </div>
  );
}
